'use strict';
import { Injectable } from '@angular/core';
import { IEvent } from '@ispm-client/modules/events/Interfaces';
import { IFrameContainerWidgetComponent } from '@ispm-client/modules/widgets/components/iframe-container-widget/iframe-container-widget.component';
import { IIframePayload } from '@ispm-client/modules/widgets/components/iframe-container-widget/interfaces/iframe-container.interface';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { IspmHttpClient } from '@ispm/core/services/ispm-http-client';
import { PlaceholderModalLoaderPayload } from '@ispm/layout/classes/placeholder-modal-loader-payload';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { PlaceholderModalComponent } from '@ispm/layout/components/modals/placeholder-modal/placeholder-modal.component';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

@Injectable()
export class PacsLauncherService {
  private _pacsConfig: any = null;
  private _pacsIntegrationActive: boolean;
  private _configFetched: boolean;
  private _currentEvent: IEvent;
  private _currentEventIdentifiers: any = {};
  private _requiredParams: string[];

  constructor(private _ispmHttpClient: IspmHttpClient,
              private _modalLoader: DynamicModalLoaderService,
              private notificationMessageService: NotificationMessageService,
  ) { }

  public async init(): Promise<boolean>{
    const timeout: number = 300000; // 30 seconds
    const startTime: number = Date.now();

    if (this._configFetched){
      return Promise.resolve(true);
    }
    this.getServerConfig();
    // Waiting for the configuration to be fetched
    while (!this._configFetched && Date.now() - startTime < timeout) {
      await new Promise(resolve => setTimeout(resolve, 250));
    }
    return Promise.resolve(this._configFetched);
  }

  private getServerConfig(){
    const SERVER_CONFIG_URL: string = 'entity-tree/ServerConfiguration/dynamicSearch?type=clubbed';
    const PACS_APPLICATION_TYPE: string = 'PACS';
    const query = {
      "target": {
          "_page": 0,
          "_count": 60,
          "_fieldslist": "name,application,additionalParameters"
      },
      "filters": [],
      "relations": [
          {
              "path": "authorization",
              "_page": 0,
              "child": "Organization",
              "_count": 30,
              "parent": "ServerConfiguration",
              "direction": "_include",
              "_fieldslist": "name"
          }
      ]
    }
    this._ispmHttpClient.post(SERVER_CONFIG_URL, query).pipe(take(1)).subscribe(serverConfig => {
      //Only one PACS application can be defined per org
      this._pacsConfig = serverConfig.data.find(serverConfig => serverConfig.application?.code?.toUpperCase() === PACS_APPLICATION_TYPE);
        this._pacsIntegrationActive = !!this._pacsConfig;
        if (this._pacsConfig?.additionalParameters && Array.isArray(this._pacsConfig.additionalParameters.properties)){
          this._requiredParams = this._pacsConfig.additionalParameters.properties.filter(param => param.required).map(item => item.name);
        }
        this._configFetched = true;
    })
  }

  private visibleForCurrentEventType(event: IEvent): boolean{
    //PACS Launcher is visible if pacsIntegration ia active and the event type is supported for integration
    let isIvisible: boolean = false;
    if (this._pacsIntegrationActive && !!event){
      if (Array.isArray(this._pacsConfig.additionalParameters?.supportedEventTypes) && !!event.category.code){
        isIvisible = this._pacsConfig.additionalParameters.supportedEventTypes.includes(event.category.code);
      }
    }
    return isIvisible;
  }

  private getIdentifierObject(event: IEvent, parameterName: string): any {
    //patientMrn is located in 'client_defined_identifier' where "system": "patientMrn"
    if (Array.isArray(event.client_defined_identifier)){
      return event.client_defined_identifier.find(item => item.system.toUpperCase() === parameterName.toUpperCase());
    }
    return null;
  }

  getAssigningAuthority(identifierObj: any): any {
    //assigning-authority is located in the 'client_defined_identifier' in which the patientMrn located, inside extension->extension[] where "url": "universal-id",
    /*Example:
      "client_defined_identifier": [
      {
        "value": "6806af8f-089b-4e88-a09d-0a695122bad9",
        "system": "patientMrn",
        "assigner": {
          "display": "Eden Medical Center",
          "reference": "2b2c5cca-f5d1-11e5-bef2-000c29c80ba6"
        },
        "extension": [
          {
            "url": "http://hl7.org/fhir/uv/v2mappings/StructureDefinition/assigning-authority",
            "extension": [
              {
                "url": "universal-id",
                "valueString": "1.2.36.1.2001.1003.0"
              }
            ]
          }
        ]
      }
    */
    const ASSIGNING_AUTHORITY_IDENTIFIER = 'http://hl7.org/fhir/uv/v2mappings/StructureDefinition/assigning-authority';
    const ASSIGNING_AUTHORITY_INNER_IDENTIFIER ='universal-id';
    let assigningAuthority;
    if (Array.isArray(identifierObj.extension)){
      for (const ext of identifierObj.extension){
        if (ext.url === ASSIGNING_AUTHORITY_IDENTIFIER){
          if (Array.isArray(ext.extension)){
            for (const innerExt of ext.extension){
              if (innerExt.url === ASSIGNING_AUTHORITY_INNER_IDENTIFIER){
                return innerExt.valueString;
              }
            }
          }
        }
      }
    }
    return assigningAuthority;
  }

  public getPacsVisibilityStatus(event: IEvent): boolean {
    return this._pacsIntegrationActive && this.visibleForCurrentEventType(event)
  }

  public setCurrentEvent(event: IEvent){
    this._currentEvent = cloneDeep(event);
    const identifierObj = this.getIdentifierObject(event, 'patientMrn');
    if (identifierObj){
      this._currentEventIdentifiers['patientMrn'] = identifierObj.value;
      this._currentEventIdentifiers['assigning-authority'] = this.getAssigningAuthority(identifierObj);
    }
    else{
      delete this._currentEventIdentifiers['patientMrn'] ;
      delete this._currentEventIdentifiers['assigning-authority'] ;
    }
  }

  public getEnabledStatus(){
    if (!this._pacsIntegrationActive || !this._currentEventIdentifiers ){
      return false;
    }

    //Get parameters names. URL format sample: "https://myPACS.com/patient_id=$(patientMrn)&accession_number=$(accessionNumber)&issuer_of_patient_id=$(assigningAuthority)",
    // const params = this.pacsURL.split('$(').map(item => { if (item.indexOf(')') > -1) return item.substring(0, item.indexOf(')'))}).filter(item => !!item);

    //Validate all required parameters have value
    let paramsValidated: boolean = true;
    if (Array.isArray(this._requiredParams)){
      for (const paramName of this._requiredParams){
        if (!this._currentEventIdentifiers[paramName] && !this.getParameterValue(paramName)){
          paramsValidated = false;
          break;
        }
      }
    }
    return paramsValidated;
  }

  private getParameterValue(paramName: string): string {
    // Search for the parameter in all the array elements under 'content'
    if (this._currentEvent && Array.isArray(this._currentEvent.content) && this._currentEvent.content.length > 0) {
      for (const content of this._currentEvent?.content) {
        if (content.data) {
          if (!!content.data[paramName] && content.data[paramName] != '') {
            return content.data[paramName];
          }
        }
      }
    }
    return null;
  }

  public sendLaunchRequest(){
    const url$: Observable<string> = this._ispmHttpClient.get('pre-signed-url/vue-motion', {
      eventId: this._currentEvent.id,
    }).pipe(
      map(response => response.url),
      catchError(err => {
        console.error('PACS launcher service: Launch request Failed : Error : ', err.message);
        this.notificationMessageService.error(err.error || 'Something went wrong');
        return Observable.empty();
      })
    );

    this._modalLoader.popupModal(PlaceholderModalComponent, PlaceholderModalLoaderPayload.builder<IIframePayload>()
      .setComponentToLoad(IFrameContainerWidgetComponent)
      .setTitle('Vue Motion')
      .setCssClasses('width-95 height-90 vue-pacs-modal all-space-for-content border-less ')
      .setPayload({ url$, showMinimizeIcon: true, containerClasses: 'white-backdrop'}), ModalTypes.CUSTOM
    ).pipe(
      take(1),
      map(response => response.payload),
    ).subscribe();
  }
}
